/**
 * Markdown Renderer Component
 * Renders AI responses with beautiful markdown formatting
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';
import './MarkdownRenderer.css';

const MarkdownRenderer = ({ content }) => {
  // Pre-process content to handle inline math that might not be properly delimited
  const processedContent = content
    .replace(/\\\(/g, '$')  // \( to $
    .replace(/\\\)/g, '$')  // \) to $
    .replace(/\\\[/g, '$$') // \[ to $$
    .replace(/\\\]/g, '$$'); // \] to $$

  return (
    <div className="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { 
          strict: false,
          throwOnError: false,
          errorColor: '#cc0000',
          macros: {
            "\\RR": "\\mathbb{R}",
            "\\NN": "\\mathbb{N}",
            "\\ZZ": "\\mathbb{Z}",
            "\\QQ": "\\mathbb{Q}",
            "\\CC": "\\mathbb{C}"
          }
        }]]}
        components={{
          // Custom code block rendering
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={vscDarkPlus}
                language={match[1]}
                PreTag="div"
                className="code-block"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={`inline-code ${className || ''}`} {...props}>
                {children}
              </code>
            );
          },
          // Custom link rendering (open in new tab)
          a({ node, children, href, ...props }) {
            return (
              <a 
                href={href} 
                target="_blank" 
                rel="noopener noreferrer"
                className="markdown-link"
                {...props}
              >
                {children}
              </a>
            );
          },
          // Custom heading rendering
          h1({ node, children, ...props }) {
            return <h1 className="markdown-h1" {...props}>{children}</h1>;
          },
          h2({ node, children, ...props }) {
            return <h2 className="markdown-h2" {...props}>{children}</h2>;
          },
          h3({ node, children, ...props }) {
            return <h3 className="markdown-h3" {...props}>{children}</h3>;
          },
          // Custom blockquote rendering
          blockquote({ node, children, ...props }) {
            return <blockquote className="markdown-blockquote" {...props}>{children}</blockquote>;
          },
          // Custom list rendering
          ul({ node, children, ...props }) {
            return <ul className="markdown-ul" {...props}>{children}</ul>;
          },
          ol({ node, children, ...props }) {
            return <ol className="markdown-ol" {...props}>{children}</ol>;
          },
          // Custom table rendering
          table({ node, children, ...props }) {
            return <table className="markdown-table" {...props}>{children}</table>;
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
